import { ENVIRONMENT } from "src/js/util/environment";
import { Nullable } from "src/js/types/Nullable";

import useSWR from "swr";

import { SelectionGeometry } from "src/js/stores/map/store";
import { negativeBuffer } from "react-migration/layouts/map/Multilayer/geometry_utilities";

import { PlanningApplicationsSummary } from "react-migration/domains/planning/types";
import { useBetaFeatureFlag } from "react-migration/lib/user/useFeatureFlag";
import Feature from "src/js/stores/user/Feature";

type PlanningApplicationFetcherArgs = [string, Nullable<SelectionGeometry>];
type PlanningApplicationSummaryFetcherResponse = Promise<PlanningApplicationsSummary>;

async function summaryFetcher([
  url,
  geometry,
]: PlanningApplicationFetcherArgs): PlanningApplicationSummaryFetcherResponse {
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify({ geometry: negativeBuffer(geometry) }),
    headers: {
      "Content-Type": "application/json",
    },
    mode: "cors",
  });
  return await response.json();
}

export function usePlanningApplicationsSummaryByGeometry(
  geometry: Nullable<SelectionGeometry>,
  disableRevalidate = false
) {
  const useNewEndpoint = useBetaFeatureFlag(Feature.aiPlanningSummaryBeta);

  const url = useNewEndpoint
    ? `${ENVIRONMENT.SUPPORTING_SERVICE_URL}/planning-summary`
    : `${ENVIRONMENT.PLANNING_SERVICE_URL}/planning-applications/within/geometry/summary`;

  const { data, error, isLoading } = useSWR(
    [url, geometry],
    summaryFetcher,
    disableRevalidate ? { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false } : {}
  );

  return { data: data, loading: isLoading, error: error ?? null };
}
